import { useState, useEffect } from 'react';
import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import BerthMediationPanel from '@/components/booking/BerthMediationPanel';
import { BerthMediationCase } from '@/types/berth-mediation.types';
import { Toolbar, ToolbarHeading, ToolbarPageTitle } from '@/partials/toolbar';
import { Container, BackButton, Button, Shimmer } from '@/components';
import { BerthType, Seasons } from '@/constant';
import {
  useGetBookingByIdQuery,
  useGetAvailableBerthsQuery,
  useUpdateBookingStatusMutation,
  useUpdateBookingMutation,
  useCancelBookingMutation,
} from '@/services/BookingService';
import { BookingStatus } from '@/types/booking-management.types';
import { formatDisplayDateTime, formatIsoDate } from '@/utils/Date';
import { handleError } from '@/utils/BaseQuery';
import { ConfirmationDialog } from '@/components/dialogBox';
import * as Yup from 'yup';
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  Euro,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { useSnackbar } from 'notistack';
import { usePermissions } from '@/hooks/usePermissions';
import { formatFinnishCurrency, formatFinnishNumber } from '@/utils';
import { extractTime, formatDateRange } from '@/utils/helperFunctions';

interface EditFormData {
  start_date: string;
  end_date: string;
}

const bookingValidationSchema = Yup.object().shape({
  start_date: Yup.string().required('Start date is required'),
  end_date: Yup.string()
    .required('End date is required')
    .test(
      'is-greater-or-equal',
      'End date must be on or after start date',
      function (end_date) {
        const { start_date } = this.parent;
        if (!start_date || !end_date) return true;
        return new Date(end_date) >= new Date(start_date);
      }
    ),
});

const formatNumberWithUnit = (
  value: number | string | null | undefined,
  unit?: string | null,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const formattedValue = formatFinnishNumber(value, options);
  return unit ? `${formattedValue} ${unit}` : formattedValue;
};

const BookingDetails = () => {
  const { bookingId: id } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isEditMode = location.pathname.includes('/edit');
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isError, refetch } = useGetBookingByIdQuery(
    id as string,
    {
      skip: !id,
    }
  );
  const bookingDetails = data?.data;
  const mediationCaseFromBooking = (
    bookingDetails as { mediation_case?: BerthMediationCase } | undefined
  )?.mediation_case;
  const mediationCaseId =
    searchParams.get('mediationCaseId') || mediationCaseFromBooking?.id;
  const blockDirectReassign =
    !!mediationCaseId &&
    mediationCaseFromBooking?.status === 'AWAITING_BOATER_B';
  const [updateBookingStatus, { isLoading: isUpdatingStatus }] =
    useUpdateBookingStatusMutation();
  const [updateBooking, { isLoading: isUpdatingBooking }] =
    useUpdateBookingMutation();
  const [cancelBooking, { isLoading: isCancelling }] =
    useCancelBookingMutation();

  // Combined loading state
  const isUpdating = isUpdatingStatus || isCancelling;
  const [confirmActionState, setConfirmActionState] = useState<{
    isOpen: boolean;
    bookingId: string | null;
    action: 'pending' | 'cancel' | null;
    reason?: string;
  }>({ isOpen: false, bookingId: null, action: null });
  const [editFormData, setEditFormData] = useState<EditFormData>({
    start_date: '',
    end_date: '',
  });
  const [editErrors, setEditErrors] = useState<{ [key: string]: string }>({});
  const [isEditing, setIsEditing] = useState(false);

  const [selectedBerthId, setSelectedBerthId] = useState<string>('');
  const [berthReassignError, setBerthReassignError] = useState<string>('');

  useEffect(() => {
    if (bookingDetails) {
      setEditFormData({
        start_date: bookingDetails.start_date.split('T')[0],
        end_date: bookingDetails.end_date.split('T')[0],
      });

      setSelectedBerthId('');
      setBerthReassignError('');

      const currentType = (
        bookingDetails?.berth as unknown as { berth_type?: number } | undefined
      )?.berth_type;
      if (
        currentType === BerthType.SEASONAL ||
        currentType === BerthType.YEARLY
      )
        setIsEditing(false);
    }
  }, [bookingDetails]);

  const canReassignBerthBase =
    (bookingDetails?.status === BookingStatus.PENDING_CONFIRMATION ||
      bookingDetails?.status === BookingStatus.PENDING) &&
    hasPermission('berth_booking_management_update');

  const canReassignBerth =
    canReassignBerthBase && !blockDirectReassign && !!bookingDetails?.berth;

  const bookingStartDate = bookingDetails?.start_date
    ? bookingDetails.start_date.split('T')[0]
    : undefined;
  const bookingEndDate = bookingDetails?.end_date
    ? bookingDetails.end_date.split('T')[0]
    : undefined;
  const boatRegistrationNumber =
    bookingDetails?.boat?.registration_number ||
    bookingDetails?.boat_details?.boat_registration_number;
  const captainFamilyName = bookingDetails?.captain_family_name;

  const currentBerthId = bookingDetails?.berth?.id;
  const bookingBerthType = (
    bookingDetails?.berth as unknown as { berth_type?: number } | undefined
  )?.berth_type;
  const harborId = (bookingDetails?.harbor as { id?: string } | undefined)?.id;

  const {
    data: availableBerthsResponse,
    isLoading: isAvailableBerthsLoading,
    isError: isAvailableBerthsError,
  } = useGetAvailableBerthsQuery(
    {
      harbor_id: harborId,
      berth_type:
        bookingBerthType === BerthType.SEASONAL ||
        bookingBerthType === BerthType.YEARLY
          ? bookingBerthType
          : undefined,
      start_date: bookingStartDate,
      end_date: bookingEndDate,
      page: 1,
      limit: 200,
    },
    {
      skip:
        !canReassignBerth || !harborId || !bookingStartDate || !bookingEndDate,
    }
  );

  const availableBerths = (availableBerthsResponse?.data || [])
    .filter((b) => b.id !== currentBerthId)
    .filter((b) => {
      const isAvailable = (b as unknown as { is_available?: boolean })
        .is_available;
      if (typeof isAvailable === 'boolean') return isAvailable;
      const status = (b as unknown as { status?: number }).status;
      return status === 1;
    });

  const handleBerthSelectionChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newBerthId = e.target.value;
    setSelectedBerthId(newBerthId);
    setBerthReassignError('');

    if (!newBerthId) return;

    const isValidOption = availableBerths.some((b) => {
      if (b.id !== newBerthId) return false;
      const isAvailable = (b as unknown as { is_available?: boolean })
        .is_available;
      if (typeof isAvailable === 'boolean') return isAvailable;
      const status = (b as unknown as { status?: number }).status;
      return status === 1;
    });
    if (!isValidOption) {
      setBerthReassignError(
        'Selected berth is not available for this date range'
      );
    }
  };

  const handleSaveBerthReassignment = async () => {
    if (!id) return;

    if (!selectedBerthId) {
      setBerthReassignError('Please select a berth');
      return;
    }

    const isValidOption = availableBerths.some((b) => {
      if (b.id !== selectedBerthId) return false;
      const isAvailable = (b as unknown as { is_available?: boolean })
        .is_available;
      if (typeof isAvailable === 'boolean') return isAvailable;
      const status = (b as unknown as { status?: number }).status;
      return status === 1;
    });
    if (!isValidOption) {
      setBerthReassignError(
        'Selected berth is not available for this date range'
      );
      return;
    }

    try {
      const response = await updateBooking({
        id: id as string,
        update: {
          berth_id: selectedBerthId,
        },
      }).unwrap();

      setSelectedBerthId('');
      setBerthReassignError('');
      refetch();
      enqueueSnackbar(response.message, { variant: 'success' });
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorMessage = (err as any)?.data?.message as string | undefined;
      setBerthReassignError(
        errorMessage || 'Failed to reassign berth. Please try again.'
      );
      handleError(err as Error);
    }
  };

  const handleActionClick = (
    bookingId: string,
    action: 'pending' | 'cancel'
  ) => {
    setConfirmActionState({ isOpen: true, bookingId, action });
  };

  const handleConfirmAction = async () => {
    const { bookingId, action, reason } = confirmActionState;
    if (!bookingId || !action) return; // early exit

    const updateData = {
      status:
        action === 'pending' ? BookingStatus.PENDING : BookingStatus.CANCELLED,
      cancellation_reason: action === 'cancel' ? reason : undefined,
    };

    try {
      if (
        bookingDetails?.status === BookingStatus.CONFIRMED &&
        action === 'cancel'
      ) {
        await cancelBooking({ id: bookingId, data: updateData }).unwrap();
        enqueueSnackbar('Booking cancelled successfully.', {
          variant: 'success',
        });
      } else {
        await updateBookingStatus({
          id: bookingId,
          update: updateData,
        }).unwrap();
        enqueueSnackbar('Request status updated successfully.', {
          variant: 'success',
        });
      }

      if (backPath) navigate(backPath);
      else navigate(-1);

      setConfirmActionState({
        isOpen: false,
        bookingId: null,
        action: null,
        reason: undefined,
      });
    } catch (error) {
      handleError(error as Error);
    }
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const validateField = async (name: string, value: string) => {
    try {
      await bookingValidationSchema.validateAt(name, {
        ...editFormData,
        [name]: value,
      });
      setEditErrors((prev) => ({ ...prev, [name]: '' }));
    } catch (err) {
      const error = err as Yup.ValidationError;
      setEditErrors((prev) => ({ ...prev, [name]: error.message }));
    }
  };

  const handleSaveChanges = async () => {
    try {
      await bookingValidationSchema.validate(editFormData, {
        abortEarly: false,
      });
      const updateData = {
        start_date: editFormData.start_date,
        end_date: editFormData.end_date,
        status: BookingStatus.PENDING_CONFIRMATION,
      };
      const response = await updateBooking({
        id: id as string,
        update: updateData,
      }).unwrap();
      setIsEditing(false);
      refetch();

      enqueueSnackbar(response.message, { variant: 'success' });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setEditErrors(validationErrors);
        enqueueSnackbar(
          `Please fix the form errors (${err.inner.length} errors occurred)`,
          { variant: 'error' }
        );
      } else {
        handleError(err as Error);
      }
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmActionState((prev) => ({ ...prev, reason: e.target.value }));
  };

  const getStatusDisplay = (status: number) => {
    let textColorClass = '';
    let statusText = '';
    switch (status) {
      case BookingStatus.PENDING:
        textColorClass = 'text-yellow-600';
        statusText = 'Pending';
        break;
      case BookingStatus.CONFIRMED:
        textColorClass = 'text-green-600';
        statusText = 'Confirmed';
        break;
      case BookingStatus.CANCELLED:
        textColorClass = 'text-red-600';
        statusText = 'Cancelled';
        break;
      case BookingStatus.COMPLETED:
        textColorClass = 'text-blue-600';
        statusText = 'Completed';
        break;
      case BookingStatus.PENDING_CONFIRMATION:
        textColorClass = 'text-gray-600';
        statusText = 'Pending Confirmation';
        break;
      default:
        textColorClass = 'text-gray-800';
        statusText = 'Pending';
    }
    return (
      <span className={`font-normal ${textColorClass}`}>{statusText}</span>
    );
  };

  const getPaymentTypeDisplay = (type: number) => {
    switch (type) {
      case 1:
        return 'Credit Card';
      case 2:
        return 'Bank Transfer';
      default:
        return 'Other';
    }
  };

  const getBerthTypeDisplay = (type?: number | null) => {
    switch (type) {
      case BerthType.TRANSIENT:
        return 'Transient';
      case BerthType.SEASONAL:
        return 'Seasonal';
      case BerthType.YEARLY:
        return 'Yearly';
      default:
        return '-';
    }
  };

  const getSeasonDisplay = (season?: string | null) => {
    switch (season) {
      case Seasons.SPRING:
        return 'Spring';
      case Seasons.SUMMER:
        return 'Summer';
      case Seasons.AUTUMN:
        return 'Autumn';
      case Seasons.WINTER:
        return 'Winter';
      default:
        return '-';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'succeeded':
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-amber-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses =
      'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium';

    switch (status.toLowerCase()) {
      case 'succeeded':
        return `${baseClasses} bg-emerald-50 dark:bg-emerald-900 contrast:bg-emerald-900 dark:text-emerald-100 contrast:text-emerald-100 text-emerald-700 border border-emerald-200 dark:border-emerald-700 contrast:border-emerald-700`;
      case 'pending':
        return `${baseClasses} bg-amber-50 dark:bg-amber-900 contrast:bg-amber-900 dark:text-amber-100 contrast:text-amber-100 text-amber-700 border border-amber-200 dark:border-amber-700 contrast:border-amber-700`;
      case 'failed':
        return `${baseClasses} bg-red-50 dark:bg-red-900 contrast:bg-red-900 dark:text-red-100 contrast:text-red-100 text-red-700 border border-red-200 dark:border-red-700 contrast:border-red-700`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-700 border border-gray-200`;
    }
  };

  // Compute dynamic back path once per render
  const backPath = (() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fromState = (location.state as any)?.from as string | undefined;
    if (fromState) return fromState;
    if (bookingDetails?.harbor)
      return '/harbors/details?tab=booking-management';
    return '/booking-management';
  })();

  const penaltyAmountRaw = (
    bookingDetails as unknown as
      | { penalty_amount?: string | number | null }
      | undefined
  )?.penalty_amount;

  const penaltyAmount =
    typeof penaltyAmountRaw === 'number'
      ? penaltyAmountRaw
      : typeof penaltyAmountRaw === 'string'
        ? Number.parseFloat(penaltyAmountRaw)
        : null;
  return (
    <Container>
      {/* Toolbar with action buttons */}
      <Toolbar>
        <ToolbarHeading>
          <div className="flex gap-2 items-center">
            <BackButton customBackPath={backPath} />
            <ToolbarPageTitle text="Booking Details" />
          </div>
        </ToolbarHeading>
        <div className="flex gap-2.5 justify-center items-center mt-4 sm:mt-0">
          {bookingDetails?.status === BookingStatus.PENDING_CONFIRMATION && (
            <div className="flex gap-2.5 justify-center items-center mt-4 sm:mt-0">
              {hasPermission('berth_booking_management_update') && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={() =>
                    handleActionClick(bookingDetails.id, 'pending')
                  }
                  disabled={isUpdatingStatus || isUpdatingBooking}
                >
                  Confirm
                </Button>
              )}
              {hasPermission('berth_booking_management_update') && (
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => handleActionClick(bookingDetails.id, 'cancel')}
                  disabled={isUpdatingStatus || isUpdatingBooking}
                >
                  Cancel
                </Button>
              )}
              {isEditing && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={() =>
                    handleActionClick(bookingDetails.id, 'pending')
                  }
                  disabled={isUpdatingStatus || isUpdatingBooking}
                >
                  Confirm
                </Button>
              )}
            </div>
          )}
          {bookingDetails &&
            bookingDetails?.status !== BookingStatus.PENDING_CONFIRMATION &&
            bookingDetails?.status !== BookingStatus.CANCELLED && (
              <div className="flex gap-2.5 justify-center items-center mt-4 sm:mt-0">
                {hasPermission('berth_booking_management_update') && (
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() =>
                      handleActionClick(bookingDetails.id, 'cancel')
                    }
                    disabled={isUpdating}
                  >
                    Cancel Booking
                  </Button>
                )}
              </div>
            )}
        </div>
      </Toolbar>

      {/* Booking Details Section */}
      <div className="card mx-auto mb-[30px]">
        {isLoading ? (
          <Shimmer height="174px" borderRadius="8px" />
        ) : isError ? (
          <div className="p-5 text-red-600" role="alert">
            Error loading data. Please try again.
          </div>
        ) : (
          <section className="p-5 grid gap-5">
            {mediationCaseId && (
              <BerthMediationPanel
                mediationCaseId={mediationCaseId}
                onResolved={() => refetch()}
              />
            )}
            {bookingDetails?.is_guest_booking && (
              <div
                className={`rounded-lg px-4 py-3 text-sm font-medium ${
                  bookingDetails.is_claimed
                    ? 'bg-green-50 text-green-700'
                    : 'bg-orange-50 text-orange-700'
                }`}
              >
                {bookingDetails.is_claimed
                  ? `Guest booking — claimed${
                      bookingDetails.claimed_at
                        ? ` on ${formatDisplayDateTime(bookingDetails.claimed_at)}`
                        : ''
                    }`
                  : 'Guest booking — not yet claimed by an account'}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7.5 items-stretch py-4 px-3">
              <div className="flex flex-col items-start gap-2">
                <span className="font-semibold text-gray-900">ID:</span>
                <span className="font-normal text-gray-700 break-all md:break-normal">
                  {bookingDetails?.id}
                </span>
              </div>
              <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                <span className="font-semibold text-gray-900">Start Date:</span>
                {isEditing ? (
                  <div className="w-full">
                    <input
                      id="start_date"
                      type="date"
                      name="start_date"
                      min={new Date().toISOString().split('T')[0]}
                      value={editFormData.start_date}
                      onChange={handleEditChange}
                      disabled={isUpdatingBooking}
                      className={`input input-bordered w-full h-12 rounded-lg bg-gray-100 border-2 transition-all duration-200 ${
                        editErrors.start_date ? 'border-red-500' : ''
                      } focus:ring-blue-500 focus:outline-none`}
                      aria-invalid={!!editErrors.start_date}
                      aria-describedby={
                        editErrors.start_date ? 'start_date-error' : undefined
                      }
                      onKeyDown={(e) => {
                        if (e.key !== 'Tab') {
                          e.preventDefault();
                        }
                      }}
                    />
                    {editErrors.start_date && (
                      <p
                        id="start_date-error"
                        className="text-red-500 text-xs mt-2"
                        role="alert"
                      >
                        {editErrors.start_date}
                      </p>
                    )}
                  </div>
                ) : (
                  <span className="font-normal text-gray-700">
                    {bookingDetails &&
                      formatIsoDate(bookingDetails.start_date.split('T')[0])}
                  </span>
                )}
              </div>
              <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                <span className="font-semibold text-gray-900"> End Date:</span>
                {isEditing ? (
                  <div className="w-full">
                    <input
                      id="end_date"
                      type="date"
                      name="end_date"
                      min={editFormData.start_date}
                      value={editFormData.end_date}
                      onChange={handleEditChange}
                      disabled={isUpdatingBooking}
                      className={`input input-bordered w-full h-12 rounded-lg bg-gray-100 border-2 transition-all duration-200 ${
                        editErrors.end_date ? 'border-red-500' : ''
                      } focus:ring-blue-500 focus:outline-none`}
                      aria-invalid={!!editErrors.end_date}
                      aria-describedby={
                        editErrors.end_date ? 'end_date-error' : undefined
                      }
                      onKeyDown={(e) => {
                        if (e.key !== 'Tab') {
                          e.preventDefault();
                        }
                      }}
                    />
                    {editErrors.end_date && (
                      <p
                        id="end_date-error"
                        className="text-red-500 text-xs mt-2"
                        role="alert"
                      >
                        {editErrors.end_date}
                      </p>
                    )}
                  </div>
                ) : (
                  <span className="font-normal text-gray-700">
                    {bookingDetails &&
                      formatIsoDate(bookingDetails.end_date.split('T')[0])}
                  </span>
                )}
              </div>
              <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                <span className="font-semibold text-gray-900">
                  Total Price:
                </span>
                <span className="font-normal text-gray-700">
                  {typeof bookingDetails?.total_price === 'number'
                    ? formatFinnishCurrency(bookingDetails.total_price)
                    : '-'}
                </span>
              </div>
              <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                <span className="font-semibold text-gray-900">
                  Payment Type:
                </span>
                <span className="font-normal text-gray-700">
                  {bookingDetails &&
                    getPaymentTypeDisplay(bookingDetails.payment_type)}
                </span>
              </div>
              <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                <span className="font-semibold text-gray-900">Status:</span>
                {bookingDetails && getStatusDisplay(bookingDetails.status)}
              </div>
              <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                <span className="font-semibold text-gray-900">
                  Special Requests:
                </span>
                <span className="font-normal text-gray-700">
                  {bookingDetails?.special_requests || '-'}
                </span>
              </div>
              <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                <span className="font-semibold text-gray-900">
                  Cancellation Reason:
                </span>
                <span className="font-normal text-gray-700">
                  {bookingDetails?.cancellation_reason || '-'}
                </span>
              </div>

              {bookingDetails?.status === BookingStatus.CANCELLED && (
                <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    Penalty:
                  </span>
                  {typeof penaltyAmount === 'number' &&
                  Number.isFinite(penaltyAmount) ? (
                    <div
                      className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-700 dark:bg-red-900/40 dark:text-red-100 contrast:border-red-800 contrast:bg-red-950 contrast:text-white"
                      role="status"
                      aria-label="Penalty applied to this cancelled booking"
                    >
                      <AlertCircle
                        className="h-4 w-4 text-red-700 dark:text-red-100 contrast:text-white"
                        aria-hidden="true"
                      />
                      <span className="font-medium">Penalty applied</span>
                      <span
                        className="font-semibold"
                        aria-label={`Penalty amount ${formatFinnishCurrency(penaltyAmount)}`}
                      >
                        {formatFinnishCurrency(penaltyAmount)}
                      </span>
                    </div>
                  ) : (
                    <div
                      className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-100 contrast:border-gray-700 contrast:bg-gray-950 contrast:text-white"
                      role="status"
                      aria-label="No penalty applied to this cancelled booking"
                    >
                      <XCircle
                        className="h-4 w-4 text-gray-700 dark:text-gray-100 contrast:text-white"
                        aria-hidden="true"
                      />
                      <span className="font-medium">Penalty not applied</span>
                    </div>
                  )}
                </div>
              )}
              <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                <span className="font-semibold text-gray-900">User:</span>
                <span className="font-normal text-gray-700 break-all md:break-normal">
                  {bookingDetails?.user.name ||
                    bookingDetails?.user.email ||
                    '-'}
                </span>
              </div>
              {bookingDetails?.berth && (
                <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                  <span className="font-semibold text-gray-900">Berth:</span>
                  <span className="font-normal text-gray-700">
                    {bookingDetails.berth.berth_number || '-'}
                  </span>
                </div>
              )}

              {bookingDetails?.berth && (
                <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                  <span className="font-semibold text-gray-900">
                    Berth Type:
                  </span>
                  <span className="font-normal text-gray-700">
                    {getBerthTypeDisplay(
                      (
                        bookingDetails.berth as unknown as {
                          berth_type?: number;
                        }
                      ).berth_type
                    )}
                  </span>
                </div>
              )}

              {bookingDetails?.berth &&
                (bookingDetails.berth as unknown as { berth_type?: number })
                  .berth_type === BerthType.SEASONAL && (
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">Season:</span>
                    <span className="font-normal text-gray-700">
                      {getSeasonDisplay(
                        (
                          bookingDetails as unknown as {
                            season?: string | null;
                          }
                        ).season
                      )}
                    </span>
                  </div>
                )}

              {canReassignBerth && (
                <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                  <span className="font-semibold text-gray-900">
                    Berth Reassignment:
                  </span>
                  <div className="w-full">
                    <div className="flex flex-col gap-2">
                      <select
                        value={selectedBerthId}
                        onChange={handleBerthSelectionChange}
                        disabled={isUpdatingBooking || isAvailableBerthsLoading}
                        className="input input-bordered w-full h-12 rounded-lg bg-gray-100 border-2 transition-all duration-200 focus:ring-blue-500 focus:outline-none"
                        aria-label="Select alternative berth"
                      >
                        <option value="">Select alternative berth</option>
                        {availableBerths.map((berth) => (
                          <option key={berth.id} value={berth.id}>
                            {berth.berth_number}
                          </option>
                        ))}
                      </select>

                      {isAvailableBerthsError && (
                        <p className="text-red-500 text-xs" role="alert">
                          Failed to load available berths
                        </p>
                      )}

                      {!isAvailableBerthsLoading &&
                        !isAvailableBerthsError &&
                        availableBerths.length === 0 && (
                          <p className="text-gray-500 text-xs" role="status">
                            No alternative berths available for this date range
                          </p>
                        )}

                      {berthReassignError && (
                        <p className="text-red-500 text-xs" role="alert">
                          {berthReassignError}
                        </p>
                      )}

                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleSaveBerthReassignment}
                        disabled={
                          isUpdatingBooking ||
                          isAvailableBerthsLoading ||
                          !!berthReassignError ||
                          !selectedBerthId
                        }
                      >
                        Save Berth
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {isEditMode &&
                (bookingDetails?.status ===
                  BookingStatus.PENDING_CONFIRMATION ||
                  bookingDetails?.status === BookingStatus.PENDING) &&
                !(
                  bookingBerthType === BerthType.SEASONAL ||
                  bookingBerthType === BerthType.YEARLY
                ) && (
                  <div className="flex gap-2.5 justify-center items-center mt-4 sm:mt-0">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setIsEditing(!isEditing)}
                      disabled={isUpdatingStatus || isUpdatingBooking}
                    >
                      {isEditing ? 'Cancel Edit' : 'Edit Dates'}
                    </Button>
                    {isEditing && (
                      <Button
                        type="button"
                        variant="primary"
                        onClick={handleSaveChanges}
                        disabled={isUpdatingStatus || isUpdatingBooking}
                      >
                        Save Changes
                      </Button>
                    )}
                  </div>
                )}
            </div>
            {bookingDetails?.is_guest_booking && bookingDetails.guest_details && (
              <div className="rounded-lg border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-3">
                  Guest Booking Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="flex flex-col items-start gap-1">
                    <span className="font-semibold text-gray-900 text-sm">
                      Name:
                    </span>
                    <span className="font-normal text-gray-700 text-sm">
                      {[
                        bookingDetails.guest_details.first_name,
                        bookingDetails.guest_details.last_name,
                      ]
                        .filter(Boolean)
                        .join(' ') || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="font-semibold text-gray-900 text-sm">
                      Email:
                    </span>
                    <span className="font-normal text-gray-700 text-sm break-all">
                      {bookingDetails.guest_details.email || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="font-semibold text-gray-900 text-sm">
                      Mobile:
                    </span>
                    <span className="font-normal text-gray-700 text-sm">
                      {bookingDetails.guest_details.mobile || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="font-semibold text-gray-900 text-sm">
                      Billing Address:
                    </span>
                    <span className="font-normal text-gray-700 text-sm">
                      {[
                        bookingDetails.guest_details.billing_address_line_1,
                        bookingDetails.guest_details.city,
                        bookingDetails.guest_details.post_code,
                      ]
                        .filter(Boolean)
                        .join(', ') || '-'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}
      </div>

      {/* Boat Details Section */}
      {(bookingDetails?.boat ||
        bookingDetails?.boat_details ||
        captainFamilyName) && (
        <>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle text="Boat Details" />
            </ToolbarHeading>
          </Toolbar>
          <div className="card mx-auto mb-[30px]">
            {isLoading ? (
              <Shimmer height="174px" borderRadius="8px" />
            ) : (
              <section className="p-5 grid gap-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7.5 items-stretch py-4 px-3">
                  {bookingDetails?.boat ? (
                    <>
                      {boatRegistrationNumber ? (
                        <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                          <span className="font-semibold text-gray-900">
                            Boat Registration Number:
                          </span>
                          <span className="font-normal text-gray-700">
                            {boatRegistrationNumber}
                          </span>
                        </div>
                      ) : (
                        captainFamilyName && (
                          <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                            <span className="font-semibold text-gray-900">
                              Boat Reference (Family Name):
                            </span>
                            <span className="font-normal text-gray-700">
                              {captainFamilyName}
                            </span>
                          </div>
                        )
                      )}
                      <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                        <span className="font-semibold text-gray-900">
                          Boat Name:
                        </span>
                        <span className="font-normal text-gray-700">
                          {bookingDetails.boat.name || '-'}
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                        <span className="font-semibold text-gray-900">
                          Boat Type:
                        </span>
                        <span className="font-normal text-gray-700">
                          {bookingDetails.boat.type || '-'}
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                        <span className="font-semibold text-gray-900">
                          Boat Length:
                        </span>
                        <span className="font-normal text-gray-700">
                          {formatNumberWithUnit(
                            bookingDetails.boat.length,
                            bookingDetails.boat.unit
                          )}
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                        <span className="font-semibold text-gray-900">
                          Boat Width:
                        </span>
                        <span className="font-normal text-gray-700">
                          {formatNumberWithUnit(
                            bookingDetails.boat.width,
                            bookingDetails.boat.unit
                          )}
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                        <span className="font-semibold text-gray-900">
                          Boat Draft:
                        </span>
                        <span className="font-normal text-gray-700">
                          {formatNumberWithUnit(
                            bookingDetails.boat.draft,
                            bookingDetails.boat.unit
                          )}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      {bookingDetails?.boat_details
                        ?.boat_registration_number ? (
                        <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                          <span className="font-semibold text-gray-900">
                            Boat Registration Number:
                          </span>
                          <span className="font-normal text-gray-700">
                            {
                              bookingDetails.boat_details
                                .boat_registration_number
                            }
                          </span>
                        </div>
                      ) : (
                        captainFamilyName && (
                          <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                            <span className="font-semibold text-gray-900">
                              Boat Reference (Family Name):
                            </span>
                            <span className="font-normal text-gray-700">
                              {captainFamilyName}
                            </span>
                          </div>
                        )
                      )}
                      <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                        <span className="font-semibold text-gray-900">
                          Boat Length:
                        </span>
                        <span className="font-normal text-gray-700">
                          {formatNumberWithUnit(
                            bookingDetails?.boat_details?.length,
                            bookingDetails?.boat_details?.unit
                          )}
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                        <span className="font-semibold text-gray-900">
                          Boat Width:
                        </span>
                        <span className="font-normal text-gray-700">
                          {formatNumberWithUnit(
                            bookingDetails?.boat_details?.width,
                            bookingDetails?.boat_details?.unit
                          )}
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                        <span className="font-semibold text-gray-900">
                          Boat Draft:
                        </span>
                        <span className="font-normal text-gray-700">
                          {formatNumberWithUnit(
                            bookingDetails?.boat_details?.draft,
                            bookingDetails?.boat_details?.unit
                          )}
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                        <span className="font-semibold text-gray-900">
                          Boat Weight:
                        </span>
                        <span className="font-normal text-gray-700">
                          {formatNumberWithUnit(
                            bookingDetails?.boat_details?.weight,
                            'kg'
                          )}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </section>
            )}
          </div>
        </>
      )}

      {/* Harbor Details Section */}
      {bookingDetails?.harbor && (
        <>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle text="Harbor Details" />
            </ToolbarHeading>
          </Toolbar>
          <div className="card mx-auto mb-[30px]">
            {isLoading ? (
              <Shimmer height="174px" borderRadius="8px" />
            ) : (
              <section className="p-5 grid gap-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7.5 items-stretch py-4 px-3">
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Harbor Name:
                    </span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.harbor.name || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Location:
                    </span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.harbor.location || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Check-in Time:
                    </span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.harbor.default_check_in_time || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Check-out Time:
                    </span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.harbor.default_check_out_time || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Check-in Window:
                    </span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.harbor.check_in_window_start &&
                      bookingDetails.harbor.check_in_window_end
                        ? `${bookingDetails.harbor.check_in_window_start} - ${bookingDetails.harbor.check_in_window_end}`
                        : '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Check-out Window:
                    </span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.harbor.check_out_window_start &&
                      bookingDetails.harbor.check_out_window_end
                        ? `${bookingDetails.harbor.check_out_window_start} - ${bookingDetails.harbor.check_out_window_end}`
                        : '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Timezone:
                    </span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.harbor.timezone || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Minimum Depth:
                    </span>
                    <span className="font-normal text-gray-700">
                      {formatNumberWithUnit(
                        bookingDetails.harbor.min_depth,
                        bookingDetails.harbor.depth_unit
                      )}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Maximum Depth:
                    </span>
                    <span className="font-normal text-gray-700">
                      {formatNumberWithUnit(
                        bookingDetails.harbor.max_depth,
                        bookingDetails.harbor.depth_unit
                      )}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Facilities:
                    </span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.harbor.facilities?.join(', ') || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Amenities:
                    </span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.harbor.amenities?.join(', ') || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Business Hours:
                    </span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.harbor.business_hours ||
                        bookingDetails.harbor.businessHours ||
                        '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">Marina:</span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.harbor.marina?.name || '-'}
                    </span>
                  </div>
                </div>
              </section>
            )}
          </div>
        </>
      )}

      {/* Booking Addons Section */}
      {bookingDetails?.booking_addons &&
        bookingDetails.booking_addons.length > 0 && (
          <>
            <Toolbar>
              <ToolbarHeading>
                <ToolbarPageTitle text="Booking Addons" />
              </ToolbarHeading>
            </Toolbar>
            <div className="card mx-auto mb-[30px]">
              {isLoading ? (
                <Shimmer height="174px" borderRadius="8px" />
              ) : (
                <section className="p-5 grid gap-5">
                  {bookingDetails.booking_addons.map((addon) => (
                    <div key={addon.id} className="border-b last:border-b-0">
                      <div className="flex flex-col sm:flex-row gap-5 py-4 px-3">
                        <div className="grid grid-cols-1 sm:grid-cols-3 lg:gap-7.5 flex-grow">
                          <div className="flex flex-col items-start gap-2">
                            <span className="font-semibold text-gray-900">
                              Addon Name:
                            </span>
                            <span className="font-normal text-gray-700">
                              {addon.addon.name}
                            </span>
                          </div>
                          <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                            <span className="font-semibold text-gray-900">
                              Quantity:
                            </span>
                            <span className="font-normal text-gray-700">
                              {formatFinnishNumber(addon.quantity, {
                                maximumFractionDigits: 0,
                              })}
                            </span>
                          </div>
                          {addon.price_unit_at_booking === 'PER_HOUR' && (
                            <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                              <span className="font-semibold text-gray-900">
                                No of Slots:
                              </span>
                              <span className="font-normal text-gray-700">
                                {formatFinnishNumber(addon.no_of_slots, {
                                  maximumFractionDigits: 0,
                                })}
                              </span>
                            </div>
                          )}
                          <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                            <span className="font-semibold text-gray-900">
                              Price:
                            </span>
                            <span className="font-normal text-gray-700">
                              {formatFinnishCurrency(
                                addon.price_unit_at_booking === 'PER_HOUR'
                                  ? addon.price_at_booking *
                                      (addon.no_of_slots || 1)
                                  : addon.price_at_booking
                              )}
                              {' ('}
                              {addon.price_unit_at_booking
                                .toLowerCase()
                                .replace('_', ' ')}
                              {addon.price_unit_at_booking === 'PER_HOUR'
                                ? ` x ${formatFinnishNumber(
                                    addon.no_of_slots || 0,
                                    {
                                      maximumFractionDigits: 0,
                                    }
                                  )} slots`
                                : ''}
                              )
                            </span>
                          </div>
                          {addon.price_unit_at_booking === 'PER_HOUR' &&
                            addon.start_date &&
                            addon.end_date && (
                              <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                                <span className="font-semibold text-gray-900">
                                  Time:
                                </span>
                                <span className="font-normal text-gray-700">
                                  {`${extractTime(addon.start_date || '')} - ${extractTime(addon.end_date)}`}
                                </span>
                              </div>
                            )}
                          {addon.start_date && addon.end_date && (
                            <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                              <span className="font-semibold text-gray-900">
                                Date:
                              </span>
                              <span className="font-normal text-gray-700">
                                {formatDateRange(
                                  addon.start_date,
                                  addon.end_date
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </section>
              )}
            </div>
          </>
        )}

      {/* Payment Transactions Section */}
      <Toolbar>
        <ToolbarHeading>
          <ToolbarPageTitle text="Payment Transactions" />
        </ToolbarHeading>
      </Toolbar>

      {/* Transactions List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="bg-gray-100 rounded-xl border border-gray-200 p-6"
              >
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
                <div className="space-y-3">
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          bookingDetails?.payment_transactions?.map((transaction) => (
            <div
              key={transaction.id}
              className="card rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200 hover:shadow-lg"
            >
              <div className="p-6">
                {/* Transaction Header */}
                <div className="flex flex-wrap items-start justify-between gap-2 mb-6 md:flex-nowrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      {transaction.type === 'refund' ? (
                        <RefreshCw className="w-5 h-5 text-gray-600" />
                      ) : (
                        <Euro className="w-5 h-5 text-gray-600" />
                      )}
                    </div>
                    <div>
                      <h2 className="font-medium text-gray-900 capitalize">
                        {transaction.type}
                      </h2>
                      <p className="text-sm text-gray-700 break-all">
                        Transaction ID: {transaction.id}
                      </p>
                    </div>
                  </div>
                  <div
                    className={`shrink-0 ${getStatusBadge(transaction.status)}`}
                  >
                    {getStatusIcon(transaction.status)}
                    <span className="capitalize">{transaction.status}</span>
                  </div>
                </div>

                {/* Transaction Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Amount */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Euro className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-500">
                        Amount
                      </span>
                    </div>
                    <p className="text-lg font-bold text-gray-900">
                      {formatFinnishCurrency(transaction.amount, {
                        currency: transaction.currency,
                      })}
                    </p>
                  </div>

                  {/* Refunded Amount */}
                  {transaction.refunded_amount > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-500">
                          Refunded
                        </span>
                      </div>
                      <p className="text-lg font-bold text-red-600">
                        -
                        {formatFinnishCurrency(transaction.refunded_amount, {
                          currency: transaction.currency,
                        })}
                      </p>
                    </div>
                  )}

                  {/* Created Date */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-500">
                        Created
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">
                      {formatDisplayDateTime(transaction.created_at)}
                    </p>
                  </div>

                  {/* Updated Date */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-500">
                        Last Updated
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">
                      {formatDisplayDateTime(transaction.updated_at)}
                    </p>
                  </div>

                  {/* Transaction Type */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-500">
                        Type
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 capitalize font-medium">
                      {transaction.type}
                    </p>
                  </div>

                  {/* Payment intent id */}
                  {transaction?.payment_intent_id && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-500">
                          Payment Intent ID
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 capitalize font-medium break-all">
                        {transaction?.payment_intent_id}
                      </p>
                    </div>
                  )}

                  {/* Paytrail payment id */}
                  {transaction?.paytrail_payment_id && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-500">
                          Paytrail Payment ID
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 capitalize font-medium break-all">
                        {transaction?.paytrail_payment_id}
                      </p>
                    </div>
                  )}

                  {/* Refund id (Stripe or Paytrail) */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-500">
                        {transaction?.paytrail_payment_id
                          ? 'Paytrail Refund ID'
                          : 'Stripe Refund ID'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 capitalize font-medium break-all">
                      {transaction?.stripe_refund_id
                        ? transaction?.stripe_refund_id
                        : '-'}
                    </p>
                  </div>
                </div>

                {/* Net Amount Summary */}
                {transaction.refunded_amount > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-500">
                        Net Amount
                      </span>
                      <span className="text-lg font-bold text-gray-900">
                        {formatFinnishCurrency(
                          transaction.amount - transaction.refunded_amount,
                          { currency: transaction.currency }
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationDialog
        open={confirmActionState.isOpen}
        onClose={() =>
          setConfirmActionState({
            isOpen: false,
            bookingId: null,
            action: null,
            reason: undefined,
          })
        }
        onConfirm={handleConfirmAction}
        message={
          confirmActionState.action === 'cancel' ? (
            <>
              {bookingDetails?.status === BookingStatus.CONFIRMED
                ? 'Are you sure you want to cancel this booking?' +
                  ' ' +
                  'This will initiate a refund process based on your cancellation policy.'
                : 'Are you sure you want to cancel this booking?'}
              <input
                type="text"
                placeholder="Cancellation reason (optional)"
                value={confirmActionState.reason || ''}
                onChange={handleReasonChange}
                className="mt-2 bg-gray-100 w-full p-2 border rounded"
                disabled={isUpdatingStatus || isUpdatingBooking}
              />
            </>
          ) : (
            `Are you sure you want to ${confirmActionState.action} this booking?`
          )
        }
        cancelButtonText="No"
        confirmButtonText="Yes"
        aria-labelledby="action-confirmation-title"
        aria-describedby="action-confirmation-description"
      />
    </Container>
  );
};

export default BookingDetails;
